import React, { useState, useEffect } from 'react';
import { Select, Tag, Tooltip, Space, message } from 'antd';
import { SwapOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useModel, useAccess } from '@umijs/max';
import { getTenantOptions } from '@/services/tenant';

const TENANT_VIEW_KEY = 'tenant_view_id';

const TenantSwitcher: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const { isSystemAdmin } = useAccess();
  const [tenants, setTenants] = useState<API.TenantOption[]>([]);

  // 从 localStorage 恢复当前选中的租户
  const [currentTenantId, setCurrentTenantId] = useState<number | undefined>(() => {
    const saved = localStorage.getItem(TENANT_VIEW_KEY);
    return saved ? parseInt(saved, 10) : undefined;
  });

  // 加载租户列表
  useEffect(() => {
    if (!isSystemAdmin) return;

    const fetchTenants = async () => {
      try {
        const response = await getTenantOptions();
        if (response.success && response.data) {
          setTenants(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch tenants:', error);
      }
    };
    fetchTenants();
  }, [isSystemAdmin]);

  // 只有系统管理员才显示租户切换器
  if (!isSystemAdmin) {
    // 普通用户显示租户名称
    if (initialState?.currentUser?.tenant) {
      return (
        <Tooltip title="">
          <Tag color="blue" style={{ marginRight: 16 }}>
            {initialState.currentUser.tenant.name}
          </Tag>
        </Tooltip>
      );
    }
    return null;
  }

  // 处理租户切换
  const handleTenantChange = (tenantId: number | undefined) => {
    if (tenantId) {
      // 保存到 localStorage（请求拦截器会自动添加到 header）
      localStorage.setItem(TENANT_VIEW_KEY, String(tenantId));
      setCurrentTenantId(tenantId);
      const tenant = tenants.find(t => t.id === tenantId);
      message.success(`已切换到租户视角: ${tenant?.name || tenantId}`);
    } else {
      // 从 localStorage 移除
      localStorage.removeItem(TENANT_VIEW_KEY);
      setCurrentTenantId(undefined);
      message.success('已恢复系统管理员视角');
    }
    // 刷新页面以重新加载数据
    window.location.reload();
  };

  // 处理清除视角切换
  const handleClear = () => {
    handleTenantChange(undefined);
  };

  return (
    <Space style={{ marginRight: 16 }}>
      <Tooltip title="切换租户视角">
        <SwapOutlined style={{ color: '#1890ff' }} />
      </Tooltip>
      <Select
        placeholder="选择租户视角"
        style={{ width: 180 }}
        allowClear
        value={currentTenantId}
        onChange={handleTenantChange}
        options={tenants.map((t) => ({
          label: `${t.name} (${t.code})`,
          value: t.id,
        }))}
      />
      {currentTenantId && (
        <Tooltip title="恢复系统管理员视角">
          <CloseCircleOutlined
            style={{ color: '#ff4d4f', cursor: 'pointer' }}
            onClick={handleClear}
          />
        </Tooltip>
      )}
    </Space>
  );
};

export default TenantSwitcher;
